import { computed, ref, ComputedRef, Ref } from "vue";
import dayjs from "dayjs";

// TODO(NOL migration): getPSSBookingId (legacy booking-id GraphQL) was removed
// in the order-store migration. The check-in ticket read is still coupled to the
// legacy booking id and must be re-built on the order/order-store API before
// this feature ships. Stubbed for now — see docs/order-migration-tracker.md.
import { getBookingScanHistory } from "@/api/check-in/check-in";
import { BookingStatus, ScanningStatus } from "@/types/db/product-slot-state";
import { IRedeemResult, CheckInOutcome, ScanLogAction } from "@/types/check-in";
import { GetPssByBookingIdQuery } from "@/types/gql/generated/graphql";
import { LogService } from "@/services/logging";
import { ACCESS_TICKET_TYPES } from "@/constants/check-in";
import { getProductCategoryById } from "@/dexie/product-category";

type PssRow = NonNullable<
  NonNullable<GetPssByBookingIdQuery["getPSSBookingId"]>[number]
>;

const ACCESS_TYPES = new Set<number>(ACCESS_TICKET_TYPES);

// The engine opens a scan this long before the slot starts when the category
// says nothing. Keep it in step with the access control service.
const DEFAULT_SCAN_OPENING_MINUTES = 15;

/** Why a line cannot go to the engine right now. */
export enum LineTiming {
  Now = "now",
  NotOpenYet = "not-open-yet",
  AnotherDay = "another-day",
  Ended = "ended",
}

export interface ICheckInTicket {
  id: number;
  token: string;
  categoryId: number;
  categoryName: string;
  /** Product of the category, needed to take an entry back. */
  productId: number;
  priceId: number;
  priceName: string;
  timeSlotId: number | null;
  startDateTime: string | null;
  endDateTime: string | null;
  seat: string;
  isCheckedIn: boolean;
  /** When the ticket last came in, from the engine log. */
  lastScanAt: string | null;
  /** Who let it in. */
  lastScanBy: string;
}

export interface ICheckInLine {
  key: string;
  categoryName: string;
  priceName: string;
  startDateTime: string | null;
  endDateTime: string | null;
  tickets: ICheckInTicket[];
  total: number;
  checkedIn: number;
  /** Tickets that the operator can still check in on this line. */
  open: number;
  isEligibleNow: boolean;
  timing: LineTiming;
  /** When the scan opens, for a line that is not open yet. */
  opensAt: string | null;
}

/**
 * Say where a ticket stands against its scan window. A ticket with no date
 * is always open, for example a membership. This only splits the list.
 * The engine makes the decision.
 *
 * @param {ICheckInTicket} ticket the ticket to examine.
 * @param {number} openingMinutes how early the scan opens for its category.
 * @returns {{ timing: LineTiming; opensAt: string | null }} where it stands.
 */
function timingOf(
  ticket: ICheckInTicket,
  openingMinutes: number
): { timing: LineTiming; opensAt: string | null } {
  if (!ticket.startDateTime) {
    return { timing: LineTiming.Now, opensAt: null };
  }

  const now = dayjs();
  const start = dayjs(ticket.startDateTime);
  const opensAt = start.subtract(openingMinutes, "minute");
  const end = ticket.endDateTime
    ? dayjs(ticket.endDateTime)
    : start.endOf("day");

  if (now.isAfter(end)) {
    return { timing: LineTiming.Ended, opensAt: null };
  }

  if (now.isBefore(opensAt)) {
    return {
      timing: opensAt.isSame(now, "day")
        ? LineTiming.NotOpenYet
        : LineTiming.AnotherDay,
      opensAt: opensAt.toISOString(),
    };
  }

  return { timing: LineTiming.Now, opensAt: null };
}

export interface IRedeemableLinesApi {
  tickets: Ref<ICheckInTicket[]>;
  /** How many tickets the order holds, before the access filter. */
  rawCount: Ref<number>;
  isLoading: Ref<boolean>;
  hasError: Ref<boolean>;
  lines: ComputedRef<ICheckInLine[]>;
  eligibleLines: ComputedRef<ICheckInLine[]>;
  notOpenLines: ComputedRef<ICheckInLine[]>;
  totalCount: ComputedRef<number>;
  checkedInCount: ComputedRef<number>;
  load: (bookingId: string) => Promise<void>;
  refreshScans: (bookingId: string) => Promise<void>;
  applyResults: (results: IRedeemResult[], scannedBy: string) => void;
}

/**
 * Read the tickets of one booking, and group the identical ones.
 *
 * @returns {IRedeemableLinesApi} the lines, the counts and the load function.
 */
export function useRedeemableLines(): IRedeemableLinesApi {
  const tickets = ref<ICheckInTicket[]>([]);
  const rawCount = ref(0);
  // categoryId to the minutes the scan opens before the slot starts.
  const scanWindows = ref<Record<number, number>>({});
  const isLoading = ref(false);
  const hasError = ref(false);

  /**
   * Get the tickets of a booking from GraphQL.
   *
   * @param {string} bookingId the booking to read.
   * @returns {Promise<void>}
   */
  async function load(bookingId: string): Promise<void> {
    isLoading.value = true;
    hasError.value = false;

    try {
      // Stubbed: getPSSBookingId was removed in the order-store migration, so no
      // tickets are read until this is re-built on the order API (see the import note).
      const pssList: PssRow[] = [];

      rawCount.value = pssList.length;
      tickets.value = pssList
        .filter(
          (pss: PssRow) =>
            !!pss?.id &&
            pss.bookingStatus === BookingStatus.Confirmed &&
            pss.type != null &&
            ACCESS_TYPES.has(pss.type)
        )
        .map((pss: PssRow) => ({
          id: Number(pss.id),
          token: pss.token || "",
          categoryId: pss.categoryId || 0,
          categoryName: pss.categoryName || "",
          productId: pss.ProductSlotCategoryRead?.ProductId || 0,
          priceId: pss.priceId || 0,
          priceName: pss.priceName || "",
          timeSlotId: pss.timeSlotId ?? null,
          // GraphQL sends the slot times as UTC without an offset. Normalise
          // here, as the timeslot store does, so the app holds one format.
          startDateTime: pss.startDateTime
            ? dayjs.utc(pss.startDateTime).toISOString()
            : null,
          endDateTime: pss.endDateTime
            ? dayjs.utc(pss.endDateTime).toISOString()
            : null,
          seat: pss.seat || "",
          isCheckedIn: pss.scanningStatus === ScanningStatus.Scanned,
          lastScanAt: null,
          lastScanBy: "",
        }));

      await applyScanHistory(bookingId);
      const windows: Record<number, number> = {};
      for (const categoryId of new Set(
        tickets.value.map((t) => t.categoryId)
      )) {
        const category = await getProductCategoryById(categoryId);
        windows[categoryId] =
          category?.categoryInfo?.scanOpeningMinutesBeforeStart ??
          DEFAULT_SCAN_OPENING_MINUTES;
      }
      scanWindows.value = windows;

      LogService.info("Check-in: tickets read", {
        params: {
          bookingId,
          inOrder: rawCount.value,
          giveAccess: tickets.value.length,
          types: [...new Set(pssList.map((pss: PssRow) => pss?.type))],
        },
      });
    } catch (error) {
      hasError.value = true;
      tickets.value = [];
      throw error;
    } finally {
      isLoading.value = false;
    }
  }

  /**
   * Read who is already in from the engine log.
   *
   * The engine keeps its own record of every entry, so this holds even when
   * the ticket row was not marked. A failure leaves the tickets as GraphQL
   * described them.
   *
   * @param {string} bookingId - The booking to read.
   * @returns {Promise<void>}
   */
  async function applyScanHistory(bookingId: string): Promise<void> {
    try {
      const history = await getBookingScanHistory(bookingId);
      const byPss = new Map(history.map((entry) => [entry.pssId, entry]));

      tickets.value = tickets.value.map((ticket) => {
        const entry = byPss.get(ticket.id);

        if (!entry) {
          return ticket;
        }

        const lastScan = [...(entry.scanLogs || [])]
          .reverse()
          .find((log) => log.action === ScanLogAction.Scan);

        return {
          ...ticket,
          isCheckedIn: entry.validScans > 0,
          lastScanAt:
            entry.validScans > 0 && lastScan ? lastScan.createdAt : null,
          lastScanBy:
            entry.validScans > 0 && lastScan?.scanningUser
              ? [
                  lastScan.scanningUser.firstName,
                  lastScan.scanningUser.lastName,
                ]
                  .filter(Boolean)
                  .join(" ")
              : "",
        };
      });
    } catch (error) {
      LogService.info("Check-in: could not read the scans of the order", {
        error,
      });
    }
  }

  // Identical tickets share a category, a slot and a price.
  const lines = computed<ICheckInLine[]>(() => {
    const groups = new Map<string, ICheckInTicket[]>();

    for (const ticket of tickets.value) {
      const key = [
        ticket.categoryId,
        ticket.timeSlotId ?? "none",
        ticket.startDateTime ?? "none",
        ticket.priceId,
      ].join("|");

      const group = groups.get(key);
      if (group) {
        group.push(ticket);
      } else {
        groups.set(key, [ticket]);
      }
    }

    return [...groups.entries()].map(([key, groupTickets]) => {
      const first = groupTickets[0];
      const checkedIn = groupTickets.filter((t) => t.isCheckedIn).length;
      const { timing, opensAt } = timingOf(
        first,
        scanWindows.value[first.categoryId] ?? DEFAULT_SCAN_OPENING_MINUTES
      );

      return {
        key,
        categoryName: first.categoryName,
        priceName: first.priceName,
        startDateTime: first.startDateTime,
        endDateTime: first.endDateTime,
        tickets: groupTickets,
        total: groupTickets.length,
        checkedIn,
        open: groupTickets.length - checkedIn,
        isEligibleNow: timing === LineTiming.Now,
        timing,
        opensAt,
      };
    });
  });

  const eligibleLines = computed(() =>
    lines.value.filter((line) => line.isEligibleNow)
  );

  // Everything the engine will not take right now, with its reason.
  const notOpenLines = computed(() =>
    lines.value.filter((line) => !line.isEligibleNow)
  );

  const totalCount = computed(() => tickets.value.length);

  const checkedInCount = computed(
    () => tickets.value.filter((ticket) => ticket.isCheckedIn).length
  );

  /**
   * Mark the tickets that the engine accepted. This keeps the counts
   * correct without a second read of the booking.
   *
   * @param {IRedeemResult[]} results the results of the engine.
   * @param {string} scannedBy the operator who let them in.
   * @returns {void}
   */
  function applyResults(results: IRedeemResult[], scannedBy: string): void {
    const accepted = new Set(
      results
        .filter((result) => result.outcome === CheckInOutcome.Ok)
        .map((result) => result.pssId)
    );
    const now = new Date().toISOString();

    tickets.value = tickets.value.map((ticket) =>
      accepted.has(ticket.id)
        ? {
            ...ticket,
            isCheckedIn: true,
            lastScanAt: now,
            lastScanBy: scannedBy,
          }
        : ticket
    );
  }

  return {
    tickets,
    rawCount,
    isLoading,
    hasError,
    lines,
    eligibleLines,
    notOpenLines,
    totalCount,
    checkedInCount,
    load,
    refreshScans: applyScanHistory,
    applyResults,
  };
}
